import SEO from "@/components/SEO";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-300">
      <SEO title="Privacy Policy — SourceDeck" description="How SourceDeck collects, uses, and protects your data. DPDPA 2023 and GDPR-aligned." path="/privacy" />
      <div className="max-w-3xl mx-auto px-6 md:px-8 py-16 md:py-24">
        <a href="/" className="sd-label mb-8 inline-block hover:text-white transition-colors" data-testid="legal-back-home">← BACK TO SOURCEDECK</a>
        <div className="sd-label mb-3 text-[#FF3B30]">● LEGAL</div>
        <h1 className="font-display text-5xl md:text-6xl leading-tight tracking-tight mb-4">Privacy Policy</h1>
        <div className="font-mono text-xs text-zinc-500 mb-12">Last updated: February 2026 · Compliant with the DPDPA 2023 (India) and GDPR principles</div>

        <div className="space-y-6 leading-relaxed">
          <section>
            <h2 className="font-display text-2xl mt-8 mb-3 text-white">Data we collect</h2>
            <ul className="space-y-2 list-none">
              <li><span className="font-mono text-xs text-zinc-500 mr-2">·</span>Google account profile (name, email, picture) — via Google OAuth. Required for login.</li>
              <li><span className="font-mono text-xs text-zinc-500 mr-2">·</span>YouTube channel data (subscribers, views, recent uploads) — only if you connect it.</li>
              <li><span className="font-mono text-xs text-zinc-500 mr-2">·</span>Spotify listening data (top tracks, artists, saved shows) — only if you connect it.</li>
              <li><span className="font-mono text-xs text-zinc-500 mr-2">·</span>Content you create in SourceDeck: projects, scripts, sponsor deals.</li>
              <li><span className="font-mono text-xs text-zinc-500 mr-2">·</span>Payment metadata (transaction ID, amount, plan) — payment card data is handled by Razorpay, we never see it.</li>
              <li><span className="font-mono text-xs text-zinc-500 mr-2">·</span>Basic logs (IP address, timestamps) for security and debugging. Rotated on a rolling basis.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl mt-8 mb-3 text-white">How we use it</h2>
            <ul className="space-y-2 list-none">
              <li><span className="font-mono text-xs text-zinc-500 mr-2">·</span>To provide the service (auth, analytics, AI outputs, sponsor tracking).</li>
              <li><span className="font-mono text-xs text-zinc-500 mr-2">·</span>To process payments (Razorpay) and manage subscriptions.</li>
              <li><span className="font-mono text-xs text-zinc-500 mr-2">·</span>To generate AI outputs — your content and channel data are sent to third-party AI providers (Anthropic Claude) under our commercial contract. AI providers do not retain your data for model training.</li>
              <li><span className="font-mono text-xs text-zinc-500 mr-2">·</span>Product improvement — aggregate, anonymized usage data only.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl mt-8 mb-3 text-white">What we don't do</h2>
            <ul className="space-y-2 list-none">
              <li><span className="font-mono text-xs text-zinc-500 mr-2">·</span>We do not sell your data. Ever.</li>
              <li><span className="font-mono text-xs text-zinc-500 mr-2">·</span>We do not use your content to train AI models.</li>
              <li><span className="font-mono text-xs text-zinc-500 mr-2">·</span>We do not send unsolicited marketing emails.</li>
              <li><span className="font-mono text-xs text-zinc-500 mr-2">·</span>We do not read the emails your users draft with our AI unless you explicitly submit them to us for a support request.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display text-2xl mt-8 mb-3 text-white">Data location & security</h2>
            <p>Data is stored in secure managed databases with encryption at rest and TLS in transit. Access is restricted to authorized personnel. We follow the principle of least privilege internally.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl mt-8 mb-3 text-white">Your rights (DPDPA / GDPR)</h2>
            <ul className="space-y-2 list-none">
              <li><span className="font-mono text-xs text-zinc-500 mr-2">·</span>Access — request a copy of your data.</li>
              <li><span className="font-mono text-xs text-zinc-500 mr-2">·</span>Correction — fix anything wrong.</li>
              <li><span className="font-mono text-xs text-zinc-500 mr-2">·</span>Deletion — delete your account and all associated data.</li>
              <li><span className="font-mono text-xs text-zinc-500 mr-2">·</span>Portability — export your projects, scripts, sponsors as JSON.</li>
              <li><span className="font-mono text-xs text-zinc-500 mr-2">·</span>Revoke integration access — disconnect Google/Spotify from Analytics page anytime.</li>
            </ul>
            <p className="mt-4">Exercise any of these by contacting us — see <a href="/contact" className="text-[#FF3B30] hover:underline">Contact page</a>.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl mt-8 mb-3 text-white">Cookies</h2>
            <p>We use one first-party session cookie (<span className="font-mono text-xs">session_token</span>) to keep you logged in. It's httpOnly, secure, SameSite=None. No tracking cookies, no third-party pixels.</p>
          </section>

          <section>
            <h2 className="font-display text-2xl mt-8 mb-3 text-white">Changes</h2>
            <p>We'll notify you via email or an in-app banner if this policy changes materially.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
